import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import {
  displayName,
  FAMILY_RELATIONSHIPS,
  ADDRESS_KINDS,
  PHONE_KINDS,
  MARITAL_STATUSES,
  ID_DOC_KINDS,
  BLOOD_TYPES,
  type FamilyMember,
  type IdDocument,
  type IdDocKind,
  type Address,
  type AddressKind,
  type PhoneNumber,
  type PhoneKind,
  type EmailAddress,
  type EmergencyContact,
  type Doctor,
  type Prescription,
  type InsurancePolicy,
  type EducationEntry,
} from '@sfp/shared/family-member';

const NET_WORTH_BANDS = ['under_1m', '1m_5m', '5m_25m', '25m_100m', 'over_100m', 'unknown'] as const;

const INPUT_CLS =
  'rounded border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none';
const LABEL_CLS = 'text-[10px] uppercase tracking-wider text-stone-500';
const CARD_CLS = 'rounded-2xl border border-stone-200 bg-white shadow-sm p-6';
const SECTION_HEADER_CLS = 'text-[10px] uppercase tracking-wider text-stone-500 mb-4';

function initials(m: FamilyMember): string {
  const first = (m.preferredName?.trim() || m.legalFirstName?.trim() || '').charAt(0);
  const last = (m.legalLastName?.trim() || '').charAt(0);
  return (first + last).toUpperCase() || '?';
}

function fmtDate(v: unknown): string {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v && 'seconds' in (v as Record<string, unknown>)) {
    const s = (v as { seconds: number }).seconds;
    return new Date(s * 1000).toLocaleString();
  }
  return String(v);
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export function FamilyMemberDetail() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [member, setMember] = useState<FamilyMember | null>(null);
  const [draft, setDraft] = useState<FamilyMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'familyMembers', memberId));
        if (cancelled) return;
        if (!snap.exists()) {
          setNotFound(true);
        } else {
          const data = { id: snap.id, ...snap.data() } as FamilyMember;
          setMember(data);
          setDraft(structuredClone(data));
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">Admins only.</div>
    );
  }

  if (loading) {
    return <div className="mx-auto max-w-5xl px-6 py-12 text-sm text-stone-500">Loading…</div>;
  }

  if (notFound || !member || !draft) {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-4 px-6 py-12">
        <Link to="/admin/family" className="text-sm text-stone-500 hover:underline">
          ← Family members
        </Link>
        <div className="text-sm text-stone-600">Not found.</div>
      </div>
    );
  }

  const active = editing ? draft : member;

  function patch(updates: Partial<FamilyMember>) {
    setDraft((d: FamilyMember | null) => (d ? { ...d, ...updates } : d));
  }

  function patchMedical(updates: Partial<FamilyMember['medical']>) {
    setDraft((d: FamilyMember | null) => (d ? { ...d, medical: { ...d.medical, ...updates } } : d));
  }

  function patchFinancial(updates: Partial<FamilyMember['financial']>) {
    setDraft((d: FamilyMember | null) => (d ? { ...d, financial: { ...d.financial, ...updates } } : d));
  }

  function patchEstate(updates: Partial<FamilyMember['estate']>) {
    setDraft((d: FamilyMember | null) => (d ? { ...d, estate: { ...d.estate, ...updates } } : d));
  }

  async function handleSave() {
    if (!memberId || !draft) return;
    setSaving(true);
    setError(null);
    try {
      const uid = auth.currentUser?.uid ?? null;
      const { id: _id, createdAt: _ca, createdBy: _cb, updatedAt: _ua, updatedBy: _ub, revision: _rev, ...rest } = draft;
      await updateDoc(doc(db, 'familyMembers', memberId), {
        ...rest,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
        revision: increment(1),
      });
      const snap = await getDoc(doc(db, 'familyMembers', memberId));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() } as FamilyMember;
        setMember(data);
        setDraft(structuredClone(data));
      }
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setDraft(structuredClone(member!));
    setEditing(false);
    setError(null);
  }

  async function handleArchiveToggle() {
    if (!memberId || !member) return;
    setSaving(true);
    setError(null);
    try {
      const uid = auth.currentUser?.uid ?? null;
      const next = member.status === 'archived' ? 'active' : 'archived';
      await updateDoc(doc(db, 'familyMembers', memberId), {
        status: next,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
        revision: increment(1),
      });
      const snap = await getDoc(doc(db, 'familyMembers', memberId));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() } as FamilyMember;
        setMember(data);
        setDraft(structuredClone(data));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleReplaceFile(idDocId: string, kind: IdDocKind, file: File) {
    if (!memberId) return;
    setSaving(true);
    setError(null);
    try {
      const uid = auth.currentUser?.uid ?? null;
      const random = crypto.randomUUID();
      const safe = sanitizeFilename(file.name);
      const path = `family/${memberId}/${kind}/${random}-${safe}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      const nowIso = new Date().toISOString();
      setDraft((d: FamilyMember | null) => {
        if (!d) return d;
        return {
          ...d,
          idDocuments: d.idDocuments.map((row: IdDocument) =>
            row.id === idDocId
              ? {
                  ...row,
                  storagePath: path,
                  downloadUrl: url,
                  uploadedAt: nowIso,
                  uploadedBy: uid ?? undefined,
                }
              : row,
          ),
        };
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // ---- Repeatable-list helpers (operate on draft) ----

  function addEmail() {
    patch({
      emails: [
        ...draft!.emails,
        { id: crypto.randomUUID(), email: '' } as EmailAddress,
      ],
    });
  }
  function updateEmail(id: string, updates: Partial<EmailAddress>) {
    patch({
      emails: draft!.emails.map((e: EmailAddress) => (e.id === id ? { ...e, ...updates } : e)),
    });
  }
  function removeEmail(id: string) {
    patch({ emails: draft!.emails.filter((e: EmailAddress) => e.id !== id) });
  }

  function addPhone() {
    patch({
      phones: [
        ...draft!.phones,
        { id: crypto.randomUUID(), kind: 'mobile', number: '' } as PhoneNumber,
      ],
    });
  }
  function updatePhone(id: string, updates: Partial<PhoneNumber>) {
    patch({
      phones: draft!.phones.map((p: PhoneNumber) => (p.id === id ? { ...p, ...updates } : p)),
    });
  }
  function removePhone(id: string) {
    patch({ phones: draft!.phones.filter((p: PhoneNumber) => p.id !== id) });
  }

  function addAddress() {
    patch({
      addresses: [
        ...draft!.addresses,
        { id: crypto.randomUUID(), kind: 'home' } as Address,
      ],
    });
  }
  function updateAddress(id: string, updates: Partial<Address>) {
    patch({
      addresses: draft!.addresses.map((a: Address) => (a.id === id ? { ...a, ...updates } : a)),
    });
  }
  function removeAddress(id: string) {
    patch({ addresses: draft!.addresses.filter((a: Address) => a.id !== id) });
  }

  function addEmergencyContact() {
    patch({
      emergencyContacts: [
        ...draft!.emergencyContacts,
        { id: crypto.randomUUID(), name: '' } as EmergencyContact,
      ],
    });
  }
  function updateEmergencyContact(id: string, updates: Partial<EmergencyContact>) {
    patch({
      emergencyContacts: draft!.emergencyContacts.map((e: EmergencyContact) =>
        e.id === id ? { ...e, ...updates } : e,
      ),
    });
  }
  function removeEmergencyContact(id: string) {
    patch({ emergencyContacts: draft!.emergencyContacts.filter((e: EmergencyContact) => e.id !== id) });
  }

  function addIdDocument() {
    patch({
      idDocuments: [
        ...draft!.idDocuments,
        { id: crypto.randomUUID(), kind: 'passport' } as IdDocument,
      ],
    });
  }
  function updateIdDocument(id: string, updates: Partial<IdDocument>) {
    patch({
      idDocuments: draft!.idDocuments.map((d: IdDocument) => (d.id === id ? { ...d, ...updates } : d)),
    });
  }
  function removeIdDocument(id: string) {
    patch({ idDocuments: draft!.idDocuments.filter((d: IdDocument) => d.id !== id) });
  }

  function addDoctor() {
    patchMedical({
      doctors: [
        ...draft!.medical.doctors,
        { id: crypto.randomUUID(), name: '' } as Doctor,
      ],
    });
  }
  function updateDoctor(id: string, updates: Partial<Doctor>) {
    patchMedical({
      doctors: draft!.medical.doctors.map((d: Doctor) => (d.id === id ? { ...d, ...updates } : d)),
    });
  }
  function removeDoctor(id: string) {
    patchMedical({ doctors: draft!.medical.doctors.filter((d: Doctor) => d.id !== id) });
  }

  function addPrescription() {
    patchMedical({
      prescriptions: [
        ...draft!.medical.prescriptions,
        { id: crypto.randomUUID(), drug: '' } as Prescription,
      ],
    });
  }
  function updatePrescription(id: string, updates: Partial<Prescription>) {
    patchMedical({
      prescriptions: draft!.medical.prescriptions.map((p: Prescription) =>
        p.id === id ? { ...p, ...updates } : p,
      ),
    });
  }
  function removePrescription(id: string) {
    patchMedical({
      prescriptions: draft!.medical.prescriptions.filter((p: Prescription) => p.id !== id),
    });
  }

  function addInsurance() {
    patchMedical({
      insurance: [
        ...draft!.medical.insurance,
        { id: crypto.randomUUID(), kind: 'health', carrier: '' } as InsurancePolicy,
      ],
    });
  }
  function updateInsurance(id: string, updates: Partial<InsurancePolicy>) {
    patchMedical({
      insurance: draft!.medical.insurance.map((i: InsurancePolicy) => (i.id === id ? { ...i, ...updates } : i)),
    });
  }
  function removeInsurance(id: string) {
    patchMedical({ insurance: draft!.medical.insurance.filter((i: InsurancePolicy) => i.id !== id) });
  }

  function addEducation() {
    const edu = draft!.education ?? [];
    patch({
      education: [...edu, { id: crypto.randomUUID(), institution: '' } as EducationEntry],
    });
  }
  function updateEducation(id: string, updates: Partial<EducationEntry>) {
    const edu = draft!.education ?? [];
    patch({
      education: edu.map((e: EducationEntry) => (e.id === id ? { ...e, ...updates } : e)),
    });
  }
  function removeEducation(id: string) {
    const edu = draft!.education ?? [];
    patch({ education: edu.filter((e: EducationEntry) => e.id !== id) });
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
      <Link to="/admin/family" className="text-sm text-stone-500 hover:underline">
        ← Family members
      </Link>

      {error && (
        <div className="rounded border border-[#9C4A3A] bg-[#9C4A3A]/10 px-4 py-2 text-sm text-[#9C4A3A]">
          {error}
        </div>
      )}

      <TopSection
        member={active}
        original={member}
        editing={editing}
        saving={saving}
        onEdit={() => setEditing(true)}
        onCancel={handleCancel}
        onSave={handleSave}
        onArchiveToggle={handleArchiveToggle}
        navigate={navigate}
      />

      <IdentityCard
        m={active}
        editing={editing}
        onChange={(updates) => patch(updates)}
      />

      <ContactCard
        m={active}
        editing={editing}
        addEmail={addEmail}
        updateEmail={updateEmail}
        removeEmail={removeEmail}
        addPhone={addPhone}
        updatePhone={updatePhone}
        removePhone={removePhone}
        addAddress={addAddress}
        updateAddress={updateAddress}
        removeAddress={removeAddress}
        addEmergencyContact={addEmergencyContact}
        updateEmergencyContact={updateEmergencyContact}
        removeEmergencyContact={removeEmergencyContact}
      />

      <IdDocumentsCard
        m={active}
        editing={editing}
        addIdDocument={addIdDocument}
        updateIdDocument={updateIdDocument}
        removeIdDocument={removeIdDocument}
        fileInputRefs={fileInputRefs}
        onReplaceFile={handleReplaceFile}
      />

      <MedicalCard
        m={active}
        editing={editing}
        onChangeMedical={patchMedical}
        addDoctor={addDoctor}
        updateDoctor={updateDoctor}
        removeDoctor={removeDoctor}
        addPrescription={addPrescription}
        updatePrescription={updatePrescription}
        removePrescription={removePrescription}
        addInsurance={addInsurance}
        updateInsurance={updateInsurance}
        removeInsurance={removeInsurance}
      />

      <FinancialCard
        m={active}
        editing={editing}
        onChangeFinancial={patchFinancial}
      />

      <EstateCard m={active} editing={editing} onChangeEstate={patchEstate} />

      <EducationCard
        m={active}
        editing={editing}
        addEducation={addEducation}
        updateEducation={updateEducation}
        removeEducation={removeEducation}
      />

      <NotesTagsCard m={active} editing={editing} onChange={patch} />

      <ActivityCard m={member} />
    </div>
  );
}

// ---- Top section ----

function TopSection({
  member,
  original,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
  onArchiveToggle,
}: {
  member: FamilyMember;
  original: FamilyMember;
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onArchiveToggle: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const status = original.status;
  return (
    <div className="flex items-start gap-6">
      {member.photoUrl ? (
        <img
          src={member.photoUrl}
          alt={displayName(member)}
          className="h-24 w-24 rounded-full object-cover"
        />
      ) : (
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#BF5700] text-2xl font-semibold text-white">
          {initials(member)}
        </div>
      )}
      <div className="flex-1">
        <h1 className="text-2xl font-semibold text-stone-800">{displayName(member)}</h1>
        <div className="mt-1 flex items-center gap-2 text-sm text-stone-600">
          <span>{member.relationshipToHead ?? '—'}</span>
          <span className="text-stone-300">•</span>
          <span>DOB {member.dateOfBirth || '—'}</span>
          <span
            className={
              'ml-2 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ' +
              (status === 'archived'
                ? 'bg-stone-200 text-stone-600'
                : 'bg-[#BF5700]/10 text-[#BF5700]')
            }
          >
            {status}
          </span>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        {!editing && (
          <button
            type="button"
            onClick={onEdit}
            className="rounded bg-[#BF5700] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Edit
          </button>
        )}
        {editing && (
          <>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="rounded bg-[#BF5700] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
            >
              Cancel
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onArchiveToggle}
          disabled={saving}
          className="rounded border border-[#9C4A3A] px-4 py-2 text-sm text-[#9C4A3A] hover:bg-[#9C4A3A]/10 disabled:opacity-50"
        >
          {status === 'archived' ? 'Reactivate' : 'Archive'}
        </button>
      </div>
    </div>
  );
}

// ---- Identity ----

function IdentityCard({
  m,
  editing,
  onChange,
}: {
  m: FamilyMember;
  editing: boolean;
  onChange: (u: Partial<FamilyMember>) => void;
}) {
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Identity</div>
      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Legal first name"
          editing={editing}
          value={m.legalFirstName}
          onChange={(v) => onChange({ legalFirstName: v })}
        />
        <Field
          label="Legal middle name"
          editing={editing}
          value={m.legalMiddleName ?? ''}
          onChange={(v) => onChange({ legalMiddleName: v })}
        />
        <Field
          label="Legal last name"
          editing={editing}
          value={m.legalLastName}
          onChange={(v) => onChange({ legalLastName: v })}
        />
        <Field
          label="Preferred name"
          editing={editing}
          value={m.preferredName ?? ''}
          onChange={(v) => onChange({ preferredName: v })}
        />
        <Field
          label="Suffix"
          editing={editing}
          value={m.suffix ?? ''}
          onChange={(v) => onChange({ suffix: v })}
        />
        <Field
          label="Date of birth"
          editing={editing}
          type="date"
          value={m.dateOfBirth ?? ''}
          onChange={(v) => onChange({ dateOfBirth: v })}
        />
        <Field
          label="Place of birth"
          editing={editing}
          value={m.placeOfBirth ?? ''}
          onChange={(v) => onChange({ placeOfBirth: v })}
        />
        <Field
          label="Gender"
          editing={editing}
          value={m.gender ?? ''}
          onChange={(v) => onChange({ gender: v })}
        />
        <Field
          label="Pronouns"
          editing={editing}
          value={m.pronouns ?? ''}
          onChange={(v) => onChange({ pronouns: v })}
        />
        <SelectField
          label="Marital status"
          editing={editing}
          value={m.maritalStatus ?? ''}
          options={MARITAL_STATUSES}
          onChange={(v) => onChange({ maritalStatus: (v || undefined) as FamilyMember['maritalStatus'] })}
        />
        <SelectField
          label="Relationship to head"
          editing={editing}
          value={m.relationshipToHead ?? ''}
          options={FAMILY_RELATIONSHIPS}
          onChange={(v) =>
            onChange({ relationshipToHead: (v || undefined) as FamilyMember['relationshipToHead'] })
          }
        />
        <Field
          label="Photo URL"
          editing={editing}
          value={m.photoUrl ?? ''}
          onChange={(v) => onChange({ photoUrl: v })}
        />
      </div>
    </section>
  );
}

// ---- Contact ----

function ContactCard({
  m,
  editing,
  addEmail,
  updateEmail,
  removeEmail,
  addPhone,
  updatePhone,
  removePhone,
  addAddress,
  updateAddress,
  removeAddress,
  addEmergencyContact,
  updateEmergencyContact,
  removeEmergencyContact,
}: {
  m: FamilyMember;
  editing: boolean;
  addEmail: () => void;
  updateEmail: (id: string, u: Partial<EmailAddress>) => void;
  removeEmail: (id: string) => void;
  addPhone: () => void;
  updatePhone: (id: string, u: Partial<PhoneNumber>) => void;
  removePhone: (id: string) => void;
  addAddress: () => void;
  updateAddress: (id: string, u: Partial<Address>) => void;
  removeAddress: (id: string) => void;
  addEmergencyContact: () => void;
  updateEmergencyContact: (id: string, u: Partial<EmergencyContact>) => void;
  removeEmergencyContact: (id: string) => void;
}) {
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Contact</div>

      <div className="space-y-6">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Emails</div>
            {editing && (
              <button type="button" onClick={addEmail} className="text-xs text-[#BF5700] hover:underline">
                + Add email
              </button>
            )}
          </div>
          {m.emails.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No emails.</div>
          )}
          <div className="space-y-2">
            {m.emails.map((e: EmailAddress) => (
              <div key={e.id} className="flex gap-2">
                {editing ? (
                  <>
                    <input
                      type="text"
                      placeholder="Label"
                      className={INPUT_CLS + ' w-32'}
                      value={e.label ?? ''}
                      onChange={(ev) => updateEmail(e.id, { label: ev.target.value })}
                    />
                    <input
                      type="email"
                      placeholder="email@example.com"
                      className={INPUT_CLS + ' flex-1'}
                      value={e.email}
                      onChange={(ev) => updateEmail(e.id, { email: ev.target.value })}
                    />
                    <label className="flex items-center gap-1 text-xs text-stone-600">
                      <input
                        type="checkbox"
                        checked={!!e.isPrimary}
                        onChange={(ev) => updateEmail(e.id, { isPrimary: ev.target.checked })}
                      />
                      Primary
                    </label>
                    <button
                      type="button"
                      onClick={() => removeEmail(e.id)}
                      className="text-xs text-[#9C4A3A] hover:underline"
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <div className="text-sm text-stone-700">
                    <span className="font-medium">{e.email}</span>
                    {e.label && <span className="ml-2 text-stone-500">({e.label})</span>}
                    {e.isPrimary && <span className="ml-2 text-[10px] uppercase text-[#BF5700]">primary</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Phones</div>
            {editing && (
              <button type="button" onClick={addPhone} className="text-xs text-[#BF5700] hover:underline">
                + Add phone
              </button>
            )}
          </div>
          {m.phones.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No phones.</div>
          )}
          <div className="space-y-2">
            {m.phones.map((p: PhoneNumber) => (
              <div key={p.id} className="flex gap-2">
                {editing ? (
                  <>
                    <select
                      className={INPUT_CLS + ' w-32'}
                      value={p.kind}
                      onChange={(ev) => updatePhone(p.id, { kind: ev.target.value as PhoneNumber['kind'] })}
                    >
                      {PHONE_KINDS.map((k: PhoneKind) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      placeholder="+1..."
                      className={INPUT_CLS + ' flex-1'}
                      value={p.number}
                      onChange={(ev) => updatePhone(p.id, { number: ev.target.value })}
                    />
                    <label className="flex items-center gap-1 text-xs text-stone-600">
                      <input
                        type="checkbox"
                        checked={!!p.isPrimary}
                        onChange={(ev) => updatePhone(p.id, { isPrimary: ev.target.checked })}
                      />
                      Primary
                    </label>
                    <button
                      type="button"
                      onClick={() => removePhone(p.id)}
                      className="text-xs text-[#9C4A3A] hover:underline"
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <div className="text-sm text-stone-700">
                    <span className="text-stone-500 uppercase text-[10px] mr-2">{p.kind}</span>
                    <span className="font-medium">{p.number}</span>
                    {p.isPrimary && <span className="ml-2 text-[10px] uppercase text-[#BF5700]">primary</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Addresses</div>
            {editing && (
              <button type="button" onClick={addAddress} className="text-xs text-[#BF5700] hover:underline">
                + Add address
              </button>
            )}
          </div>
          {m.addresses.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No addresses.</div>
          )}
          <div className="space-y-3">
            {m.addresses.map((a: Address) => (
              <div key={a.id} className="rounded border border-stone-200 p-3">
                {editing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className={INPUT_CLS}
                      value={a.kind}
                      onChange={(ev) => updateAddress(a.id, { kind: ev.target.value as Address['kind'] })}
                    >
                      {ADDRESS_KINDS.map((k: AddressKind) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-stone-600">
                      <input
                        type="checkbox"
                        checked={!!a.isPrimary}
                        onChange={(ev) => updateAddress(a.id, { isPrimary: ev.target.checked })}
                      />
                      Primary
                    </label>
                    <input
                      className={INPUT_CLS + ' col-span-2'}
                      placeholder="Line 1"
                      value={a.line1 ?? ''}
                      onChange={(ev) => updateAddress(a.id, { line1: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS + ' col-span-2'}
                      placeholder="Line 2"
                      value={a.line2 ?? ''}
                      onChange={(ev) => updateAddress(a.id, { line2: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="City"
                      value={a.city ?? ''}
                      onChange={(ev) => updateAddress(a.id, { city: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="State"
                      value={a.state ?? ''}
                      onChange={(ev) => updateAddress(a.id, { state: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Postal code"
                      value={a.postalCode ?? ''}
                      onChange={(ev) => updateAddress(a.id, { postalCode: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Country"
                      value={a.country ?? ''}
                      onChange={(ev) => updateAddress(a.id, { country: ev.target.value })}
                    />
                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeAddress(a.id)}
                        className="text-xs text-[#9C4A3A] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-stone-700">
                    <div className="text-[10px] uppercase text-stone-500">
                      {a.kind}
                      {a.isPrimary && <span className="ml-2 text-[#BF5700]">primary</span>}
                    </div>
                    <div>{a.line1}</div>
                    {a.line2 && <div>{a.line2}</div>}
                    <div>
                      {[a.city, a.state, a.postalCode].filter(Boolean).join(', ')}
                    </div>
                    {a.country && <div>{a.country}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Emergency contacts</div>
            {editing && (
              <button
                type="button"
                onClick={addEmergencyContact}
                className="text-xs text-[#BF5700] hover:underline"
              >
                + Add contact
              </button>
            )}
          </div>
          {m.emergencyContacts.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No emergency contacts.</div>
          )}
          <div className="space-y-3">
            {m.emergencyContacts.map((c: EmergencyContact) => (
              <div key={c.id} className="rounded border border-stone-200 p-3">
                {editing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className={INPUT_CLS}
                      placeholder="Name"
                      value={c.name}
                      onChange={(ev) => updateEmergencyContact(c.id, { name: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Relationship"
                      value={c.relationship ?? ''}
                      onChange={(ev) => updateEmergencyContact(c.id, { relationship: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Phone"
                      value={c.phone ?? ''}
                      onChange={(ev) => updateEmergencyContact(c.id, { phone: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Email"
                      value={c.email ?? ''}
                      onChange={(ev) => updateEmergencyContact(c.id, { email: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS + ' col-span-2'}
                      placeholder="Notes"
                      value={c.notes ?? ''}
                      onChange={(ev) => updateEmergencyContact(c.id, { notes: ev.target.value })}
                    />
                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeEmergencyContact(c.id)}
                        className="text-xs text-[#9C4A3A] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-stone-700">
                    <span className="font-medium">{c.name}</span>
                    {c.relationship && <span className="ml-2 text-stone-500">({c.relationship})</span>}
                    <div className="text-stone-600">
                      {[c.phone, c.email].filter(Boolean).join(' • ')}
                    </div>
                    {c.notes && <div className="text-stone-500">{c.notes}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- ID documents ----

function IdDocumentsCard({
  m,
  editing,
  addIdDocument,
  updateIdDocument,
  removeIdDocument,
  fileInputRefs,
  onReplaceFile,
}: {
  m: FamilyMember;
  editing: boolean;
  addIdDocument: () => void;
  updateIdDocument: (id: string, u: Partial<IdDocument>) => void;
  removeIdDocument: (id: string) => void;
  fileInputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  onReplaceFile: (idDocId: string, kind: IdDocKind, file: File) => void;
}) {
  return (
    <section className={CARD_CLS}>
      <div className="mb-4 flex items-center justify-between">
        <div className={LABEL_CLS}>Identity documents</div>
        {editing && (
          <button type="button" onClick={addIdDocument} className="text-xs text-[#BF5700] hover:underline">
            + Add document
          </button>
        )}
      </div>
      {m.idDocuments.length === 0 && !editing && (
        <div className="text-sm text-stone-400">No identity documents.</div>
      )}
      <div className="space-y-3">
        {m.idDocuments.map((d: IdDocument) => (
          <div key={d.id} className="rounded border border-stone-200 p-3">
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <select
                  className={INPUT_CLS}
                  value={d.kind}
                  onChange={(ev) =>
                    updateIdDocument(d.id, { kind: ev.target.value as IdDocKind })
                  }
                >
                  {ID_DOC_KINDS.map((k: IdDocKind) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <input
                  className={INPUT_CLS}
                  placeholder="Number"
                  value={d.number ?? ''}
                  onChange={(ev) => updateIdDocument(d.id, { number: ev.target.value })}
                />
                <input
                  className={INPUT_CLS}
                  placeholder="Issuing country"
                  value={d.issuingCountry ?? ''}
                  onChange={(ev) => updateIdDocument(d.id, { issuingCountry: ev.target.value })}
                />
                <input
                  className={INPUT_CLS}
                  placeholder="Issuing state"
                  value={d.issuingState ?? ''}
                  onChange={(ev) => updateIdDocument(d.id, { issuingState: ev.target.value })}
                />
                <input
                  type="date"
                  className={INPUT_CLS}
                  placeholder="Issued at"
                  value={d.issuedAt ?? ''}
                  onChange={(ev) => updateIdDocument(d.id, { issuedAt: ev.target.value })}
                />
                <input
                  type="date"
                  className={INPUT_CLS}
                  placeholder="Expires at"
                  value={d.expiresAt ?? ''}
                  onChange={(ev) => updateIdDocument(d.id, { expiresAt: ev.target.value })}
                />
                <div className="col-span-2 flex items-center gap-3 text-xs text-stone-600">
                  {d.downloadUrl && (
                    <a
                      href={d.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#BF5700] hover:underline"
                    >
                      Download
                    </a>
                  )}
                  <input
                    ref={(el) => {
                      fileInputRefs.current[d.id] = el;
                    }}
                    type="file"
                    className="hidden"
                    onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                      const file = ev.target.files?.[0];
                      if (file) onReplaceFile(d.id, d.kind, file);
                      ev.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current[d.id]?.click()}
                    className="text-[#BF5700] hover:underline"
                  >
                    Replace file
                  </button>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => removeIdDocument(d.id)}
                    className="text-[#9C4A3A] hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-stone-700">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-stone-500">{d.kind}</span>
                  {d.number && <span className="font-medium">#{d.number}</span>}
                  {d.downloadUrl && (
                    <a
                      href={d.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto text-xs text-[#BF5700] hover:underline"
                    >
                      Download
                    </a>
                  )}
                </div>
                <div className="text-stone-600">
                  {[d.issuingCountry, d.issuingState].filter(Boolean).join(' / ')}
                </div>
                <div className="text-stone-500 text-xs">
                  {d.issuedAt && <>Issued {d.issuedAt} </>}
                  {d.expiresAt && <>• Expires {d.expiresAt}</>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Medical ----

function MedicalCard({
  m,
  editing,
  onChangeMedical,
  addDoctor,
  updateDoctor,
  removeDoctor,
  addPrescription,
  updatePrescription,
  removePrescription,
  addInsurance,
  updateInsurance,
  removeInsurance,
}: {
  m: FamilyMember;
  editing: boolean;
  onChangeMedical: (u: Partial<FamilyMember['medical']>) => void;
  addDoctor: () => void;
  updateDoctor: (id: string, u: Partial<Doctor>) => void;
  removeDoctor: (id: string) => void;
  addPrescription: () => void;
  updatePrescription: (id: string, u: Partial<Prescription>) => void;
  removePrescription: (id: string) => void;
  addInsurance: () => void;
  updateInsurance: (id: string, u: Partial<InsurancePolicy>) => void;
  removeInsurance: (id: string) => void;
}) {
  const med = m.medical;
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Medical</div>
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Primary care doctor</label>
            {editing ? (
              <select
                className={INPUT_CLS + ' mt-1 w-full'}
                value={med.primaryCareDoctorId ?? ''}
                onChange={(ev) => onChangeMedical({ primaryCareDoctorId: ev.target.value || undefined })}
              >
                <option value="">— None —</option>
                {med.doctors.map((d: Doctor) => (
                  <option key={d.id} value={d.id}>
                    {d.name || d.id}
                  </option>
                ))}
              </select>
            ) : (
              <div className="mt-1 text-sm text-stone-700">
                {med.primaryCareDoctorId
                  ? med.doctors.find((d: Doctor) => d.id === med.primaryCareDoctorId)?.name ?? '—'
                  : '—'}
              </div>
            )}
          </div>
          <SelectField
            label="Blood type"
            editing={editing}
            value={med.bloodType ?? ''}
            options={BLOOD_TYPES}
            onChange={(v) => onChangeMedical({ bloodType: (v || undefined) as typeof med.bloodType })}
          />
        </div>

        <ChipInput
          label="Allergies"
          editing={editing}
          values={med.allergies ?? []}
          onChange={(values) => onChangeMedical({ allergies: values })}
        />
        <ChipInput
          label="Chronic conditions"
          editing={editing}
          values={med.chronicConditions ?? []}
          onChange={(values) => onChangeMedical({ chronicConditions: values })}
        />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Doctors</div>
            {editing && (
              <button type="button" onClick={addDoctor} className="text-xs text-[#BF5700] hover:underline">
                + Add doctor
              </button>
            )}
          </div>
          {med.doctors.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No doctors.</div>
          )}
          <div className="space-y-3">
            {med.doctors.map((d: Doctor) => (
              <div key={d.id} className="rounded border border-stone-200 p-3">
                {editing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className={INPUT_CLS}
                      placeholder="Name"
                      value={d.name}
                      onChange={(ev) => updateDoctor(d.id, { name: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Specialty"
                      value={d.specialty ?? ''}
                      onChange={(ev) => updateDoctor(d.id, { specialty: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Practice"
                      value={d.practice ?? ''}
                      onChange={(ev) => updateDoctor(d.id, { practice: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Phone"
                      value={d.phone ?? ''}
                      onChange={(ev) => updateDoctor(d.id, { phone: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Email"
                      value={d.email ?? ''}
                      onChange={(ev) => updateDoctor(d.id, { email: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Address"
                      value={d.addressLine ?? ''}
                      onChange={(ev) => updateDoctor(d.id, { addressLine: ev.target.value })}
                    />
                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeDoctor(d.id)}
                        className="text-xs text-[#9C4A3A] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-stone-700">
                    <div className="font-medium">{d.name}</div>
                    <div className="text-stone-600">
                      {[d.specialty, d.practice].filter(Boolean).join(' • ')}
                    </div>
                    <div className="text-stone-500 text-xs">
                      {[d.phone, d.email].filter(Boolean).join(' • ')}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Prescriptions</div>
            {editing && (
              <button type="button" onClick={addPrescription} className="text-xs text-[#BF5700] hover:underline">
                + Add prescription
              </button>
            )}
          </div>
          {med.prescriptions.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No prescriptions.</div>
          )}
          <div className="space-y-3">
            {med.prescriptions.map((p: Prescription) => (
              <div key={p.id} className="rounded border border-stone-200 p-3">
                {editing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className={INPUT_CLS}
                      placeholder="Drug"
                      value={p.drug}
                      onChange={(ev) => updatePrescription(p.id, { drug: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Dose"
                      value={p.dose ?? ''}
                      onChange={(ev) => updatePrescription(p.id, { dose: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Frequency"
                      value={p.frequency ?? ''}
                      onChange={(ev) => updatePrescription(p.id, { frequency: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Pharmacy"
                      value={p.pharmacy ?? ''}
                      onChange={(ev) => updatePrescription(p.id, { pharmacy: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Rx number"
                      value={p.rxNumber ?? ''}
                      onChange={(ev) => updatePrescription(p.id, { rxNumber: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Refills remaining"
                      type="number"
                      value={p.refillsRemaining ?? ''}
                      onChange={(ev) =>
                        updatePrescription(p.id, {
                          refillsRemaining: ev.target.value === '' ? undefined : Number(ev.target.value),
                        })
                      }
                    />
                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removePrescription(p.id)}
                        className="text-xs text-[#9C4A3A] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-stone-700">
                    <div className="font-medium">{p.drug}</div>
                    <div className="text-stone-600">
                      {[p.dose, p.frequency].filter(Boolean).join(' • ')}
                    </div>
                    <div className="text-stone-500 text-xs">
                      {[p.pharmacy, p.rxNumber && `Rx ${p.rxNumber}`].filter(Boolean).join(' • ')}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className={LABEL_CLS}>Insurance</div>
            {editing && (
              <button type="button" onClick={addInsurance} className="text-xs text-[#BF5700] hover:underline">
                + Add policy
              </button>
            )}
          </div>
          {med.insurance.length === 0 && !editing && (
            <div className="text-sm text-stone-400">No insurance policies.</div>
          )}
          <div className="space-y-3">
            {med.insurance.map((i: InsurancePolicy) => (
              <div key={i.id} className="rounded border border-stone-200 p-3">
                {editing ? (
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className={INPUT_CLS}
                      value={i.kind}
                      onChange={(ev) =>
                        updateInsurance(i.id, { kind: ev.target.value as InsurancePolicy['kind'] })
                      }
                    >
                      {['health', 'dental', 'vision', 'life', 'disability', 'auto', 'home', 'umbrella', 'other'].map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                    <input
                      className={INPUT_CLS}
                      placeholder="Carrier"
                      value={i.carrier}
                      onChange={(ev) => updateInsurance(i.id, { carrier: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Policy #"
                      value={i.policyNumber ?? ''}
                      onChange={(ev) => updateInsurance(i.id, { policyNumber: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Group #"
                      value={i.groupNumber ?? ''}
                      onChange={(ev) => updateInsurance(i.id, { groupNumber: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Member ID"
                      value={i.memberId ?? ''}
                      onChange={(ev) => updateInsurance(i.id, { memberId: ev.target.value })}
                    />
                    <input
                      className={INPUT_CLS}
                      placeholder="Phone"
                      value={i.phone ?? ''}
                      onChange={(ev) => updateInsurance(i.id, { phone: ev.target.value })}
                    />
                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeInsurance(i.id)}
                        className="text-xs text-[#9C4A3A] hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-stone-700">
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">{i.kind}</div>
                    <div className="font-medium">{i.carrier}</div>
                    <div className="text-stone-600 text-xs">
                      {[i.policyNumber && `Policy ${i.policyNumber}`, i.groupNumber && `Group ${i.groupNumber}`]
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Financial ----

function FinancialCard({
  m,
  editing,
  onChangeFinancial,
}: {
  m: FamilyMember;
  editing: boolean;
  onChangeFinancial: (u: Partial<FamilyMember['financial']>) => void;
}) {
  const fin = m.financial;
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Financial</div>
      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Occupation"
          editing={editing}
          value={fin.occupation ?? ''}
          onChange={(v) => onChangeFinancial({ occupation: v })}
        />
        <Field
          label="Employer"
          editing={editing}
          value={fin.employer ?? ''}
          onChange={(v) => onChangeFinancial({ employer: v })}
        />
        <div>
          <label className={LABEL_CLS}>SSN last 4</label>
          {editing ? (
            <input
              className={INPUT_CLS + ' mt-1 w-full'}
              maxLength={4}
              inputMode="numeric"
              pattern="\d{4}"
              value={fin.ssnLast4 ?? ''}
              onChange={(ev) => {
                const v = ev.target.value.replace(/\D/g, '').slice(0, 4);
                onChangeFinancial({ ssnLast4: v || undefined });
              }}
            />
          ) : (
            <div className="mt-1 text-sm text-stone-700">{fin.ssnLast4 ? `••• ${fin.ssnLast4}` : '—'}</div>
          )}
        </div>
        <Field
          label="Tax residency country"
          editing={editing}
          value={fin.taxResidencyCountry ?? ''}
          onChange={(v) => onChangeFinancial({ taxResidencyCountry: v })}
        />
        <Field
          label="Tax residency state"
          editing={editing}
          value={fin.taxResidencyState ?? ''}
          onChange={(v) => onChangeFinancial({ taxResidencyState: v })}
        />
        <div>
          <label className={LABEL_CLS}>Net worth band</label>
          {editing ? (
            <select
              className={INPUT_CLS + ' mt-1 w-full'}
              value={fin.netWorthBand ?? ''}
              onChange={(ev) =>
                onChangeFinancial({
                  netWorthBand: (ev.target.value || undefined) as typeof fin.netWorthBand,
                })
              }
            >
              <option value="">—</option>
              {NET_WORTH_BANDS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          ) : (
            <div className="mt-1 text-sm text-stone-700">{fin.netWorthBand ?? '—'}</div>
          )}
        </div>
        <div className="col-span-2">
          <ChipInput
            label="Citizenships"
            editing={editing}
            values={fin.citizenships ?? []}
            onChange={(values) => onChangeFinancial({ citizenships: values })}
          />
        </div>
      </div>
    </section>
  );
}

// ---- Estate ----

function EstateCard({
  m,
  editing,
  onChangeEstate,
}: {
  m: FamilyMember;
  editing: boolean;
  onChangeEstate: (u: Partial<FamilyMember['estate']>) => void;
}) {
  const est = m.estate;
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Estate</div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLS}>Has will</label>
          {editing ? (
            <select
              className={INPUT_CLS + ' mt-1 w-full'}
              value={est.hasWill === undefined ? '' : est.hasWill ? 'yes' : 'no'}
              onChange={(ev) =>
                onChangeEstate({ hasWill: ev.target.value === '' ? undefined : ev.target.value === 'yes' })
              }
            >
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          ) : (
            <div className="mt-1 text-sm text-stone-700">
              {est.hasWill === undefined ? '—' : est.hasWill ? 'Yes' : 'No'}
            </div>
          )}
        </div>
        <div>
          <label className={LABEL_CLS}>Has trust</label>
          {editing ? (
            <select
              className={INPUT_CLS + ' mt-1 w-full'}
              value={est.hasTrust === undefined ? '' : est.hasTrust ? 'yes' : 'no'}
              onChange={(ev) =>
                onChangeEstate({ hasTrust: ev.target.value === '' ? undefined : ev.target.value === 'yes' })
              }
            >
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          ) : (
            <div className="mt-1 text-sm text-stone-700">
              {est.hasTrust === undefined ? '—' : est.hasTrust ? 'Yes' : 'No'}
            </div>
          )}
        </div>
        <div className="col-span-2">
          <ChipInput
            label="Trust names"
            editing={editing}
            values={est.trustNames ?? []}
            onChange={(values) => onChangeEstate({ trustNames: values })}
          />
        </div>
        <Field
          label="Executor"
          editing={editing}
          value={est.executor ?? ''}
          onChange={(v) => onChangeEstate({ executor: v })}
        />
        <Field
          label="Power of attorney"
          editing={editing}
          value={est.powerOfAttorney ?? ''}
          onChange={(v) => onChangeEstate({ powerOfAttorney: v })}
        />
        <Field
          label="Healthcare proxy"
          editing={editing}
          value={est.healthcareProxy ?? ''}
          onChange={(v) => onChangeEstate({ healthcareProxy: v })}
        />
        <div className="col-span-2">
          <label className={LABEL_CLS}>Beneficiary notes</label>
          {editing ? (
            <textarea
              className={INPUT_CLS + ' mt-1 w-full'}
              rows={3}
              value={est.beneficiaryNotes ?? ''}
              onChange={(ev) => onChangeEstate({ beneficiaryNotes: ev.target.value })}
            />
          ) : (
            <div className="mt-1 whitespace-pre-wrap text-sm text-stone-700">
              {est.beneficiaryNotes || '—'}
            </div>
          )}
        </div>
        <div>
          <label className={LABEL_CLS}>Will file</label>
          <div className="mt-1 text-sm text-stone-700">
            {est.willStoragePath ? (
              <span className="text-stone-600">{est.willStoragePath}</span>
            ) : (
              '—'
            )}
          </div>
        </div>
        <div>
          <label className={LABEL_CLS}>Living will file</label>
          <div className="mt-1 text-sm text-stone-700">
            {est.livingWillStoragePath ? (
              <span className="text-stone-600">{est.livingWillStoragePath}</span>
            ) : (
              '—'
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Education ----

function EducationCard({
  m,
  editing,
  addEducation,
  updateEducation,
  removeEducation,
}: {
  m: FamilyMember;
  editing: boolean;
  addEducation: () => void;
  updateEducation: (id: string, u: Partial<EducationEntry>) => void;
  removeEducation: (id: string) => void;
}) {
  const edu = m.education ?? [];
  return (
    <section className={CARD_CLS}>
      <div className="mb-4 flex items-center justify-between">
        <div className={LABEL_CLS}>Education</div>
        {editing && (
          <button type="button" onClick={addEducation} className="text-xs text-[#BF5700] hover:underline">
            + Add institution
          </button>
        )}
      </div>
      {edu.length === 0 && !editing && <div className="text-sm text-stone-400">No education entries.</div>}
      <div className="space-y-3">
        {edu.map((e: EducationEntry) => (
          <div key={e.id} className="rounded border border-stone-200 p-3">
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <input
                  className={INPUT_CLS}
                  placeholder="Institution"
                  value={e.institution}
                  onChange={(ev) => updateEducation(e.id, { institution: ev.target.value })}
                />
                <input
                  className={INPUT_CLS}
                  placeholder="Degree"
                  value={e.degree ?? ''}
                  onChange={(ev) => updateEducation(e.id, { degree: ev.target.value })}
                />
                <input
                  className={INPUT_CLS}
                  placeholder="Field"
                  value={e.field ?? ''}
                  onChange={(ev) => updateEducation(e.id, { field: ev.target.value })}
                />
                <div className="flex gap-2">
                  <input
                    type="number"
                    className={INPUT_CLS + ' w-1/2'}
                    placeholder="Start"
                    value={e.startYear ?? ''}
                    onChange={(ev) =>
                      updateEducation(e.id, {
                        startYear: ev.target.value === '' ? undefined : Number(ev.target.value),
                      })
                    }
                  />
                  <input
                    type="number"
                    className={INPUT_CLS + ' w-1/2'}
                    placeholder="End"
                    value={e.endYear ?? ''}
                    onChange={(ev) =>
                      updateEducation(e.id, {
                        endYear: ev.target.value === '' ? undefined : Number(ev.target.value),
                      })
                    }
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeEducation(e.id)}
                    className="text-xs text-[#9C4A3A] hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-stone-700">
                <div className="font-medium">{e.institution}</div>
                <div className="text-stone-600">{[e.degree, e.field].filter(Boolean).join(' • ')}</div>
                <div className="text-stone-500 text-xs">
                  {[e.startYear, e.endYear].filter(Boolean).join(' – ')}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Notes + Tags ----

function NotesTagsCard({
  m,
  editing,
  onChange,
}: {
  m: FamilyMember;
  editing: boolean;
  onChange: (u: Partial<FamilyMember>) => void;
}) {
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Notes & tags</div>
      <div className="space-y-4">
        <div>
          <label className={LABEL_CLS}>Notes</label>
          {editing ? (
            <textarea
              className={INPUT_CLS + ' mt-1 w-full'}
              rows={4}
              value={m.notes ?? ''}
              onChange={(ev) => onChange({ notes: ev.target.value })}
            />
          ) : (
            <div className="mt-1 whitespace-pre-wrap text-sm text-stone-700">{m.notes || '—'}</div>
          )}
        </div>
        <ChipInput
          label="Tags"
          editing={editing}
          values={m.tags ?? []}
          onChange={(values) => onChange({ tags: values })}
        />
      </div>
    </section>
  );
}

// ---- Activity ----

function ActivityCard({ m }: { m: FamilyMember }) {
  return (
    <section className={CARD_CLS}>
      <div className={SECTION_HEADER_CLS}>Activity</div>
      <div className="grid grid-cols-2 gap-4 text-sm text-stone-700">
        <div>
          <div className={LABEL_CLS}>Created</div>
          <div className="mt-1">{fmtDate(m.createdAt)}</div>
          <div className="text-xs text-stone-500">by {m.createdBy ?? '—'}</div>
        </div>
        <div>
          <div className={LABEL_CLS}>Updated</div>
          <div className="mt-1">{fmtDate(m.updatedAt)}</div>
          <div className="text-xs text-stone-500">by {m.updatedBy ?? '—'}</div>
        </div>
        <div>
          <div className={LABEL_CLS}>Revision</div>
          <div className="mt-1">{m.revision}</div>
        </div>
      </div>
    </section>
  );
}

// ---- Reusable bits ----

function Field({
  label,
  value,
  editing,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  editing: boolean;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      {editing ? (
        <input
          type={type}
          className={INPUT_CLS + ' mt-1 w-full'}
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
        />
      ) : (
        <div className="mt-1 text-sm text-stone-700">{value || '—'}</div>
      )}
    </div>
  );
}

function SelectField({
  label,
  value,
  editing,
  options,
  onChange,
}: {
  label: string;
  value: string;
  editing: boolean;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      {editing ? (
        <select
          className={INPUT_CLS + ' mt-1 w-full'}
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <div className="mt-1 text-sm text-stone-700">{value || '—'}</div>
      )}
    </div>
  );
}

function ChipInput({
  label,
  values,
  editing,
  onChange,
}: {
  label: string;
  values: string[];
  editing: boolean;
  onChange: (next: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const stable = useMemo(() => values, [values]);
  function commit() {
    const v = input.trim();
    if (!v) return;
    if (stable.includes(v)) {
      setInput('');
      return;
    }
    onChange([...stable, v]);
    setInput('');
  }
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {stable.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700"
          >
            {v}
            {editing && (
              <button
                type="button"
                onClick={() => onChange(stable.filter((x) => x !== v))}
                className="text-stone-500 hover:text-[#9C4A3A]"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {editing && (
          <input
            className={INPUT_CLS + ' flex-1 min-w-[8rem]'}
            value={input}
            placeholder={`Add ${label.toLowerCase()}…`}
            onChange={(ev) => setInput(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ',') {
                ev.preventDefault();
                commit();
              }
            }}
            onBlur={commit}
          />
        )}
        {!editing && stable.length === 0 && <span className="text-sm text-stone-400">—</span>}
      </div>
    </div>
  );
}
